import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Circle, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { TrustPortalLayout } from "../components/shared/TrustPortalLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";

const STEPS = [
  { key: "personal_info", label: "Personal Info", fields: [
    { name: "full_name", label: "Full Legal Name" },
    { name: "date_of_birth", label: "Date of Birth", type: "date" },
    { name: "marital_status", label: "Marital Status" },
    { name: "mailing_address", label: "Mailing Address", textarea: true },
  ]},
  { key: "trust_structure", label: "Trust Structure", fields: [
    { name: "trust_type", label: "Trust Type (Basic / Private)" },
    { name: "structure_notes", label: "Notes on how you'd like your trust structured", textarea: true },
  ]},
  { key: "beneficiaries", label: "Beneficiaries", fields: [
    { name: "primary_beneficiary_name", label: "Primary Beneficiary Name" },
    { name: "primary_beneficiary_relationship", label: "Relationship" },
    { name: "secondary_beneficiary_name", label: "Secondary Beneficiary Name (optional)" },
    { name: "secondary_beneficiary_relationship", label: "Relationship (optional)" },
  ]},
  { key: "asset_protection", label: "Asset Protection", fields: [
    { name: "assets_summary", label: "List your major assets (home, business, investments, etc.)", textarea: true },
  ]},
];

export default function TrustPortalIntakePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sections, setSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    (async () => {
      const { data } = await api.get("/trust-portal/status");
      const initial = {};
      STEPS.forEach((s) => { initial[s.key] = data.intake?.sections?.[s.key]?.data || {}; });
      setSections(initial);
      setLoaded(true);
    })();
  }, []);

  const current = STEPS[step];
  const setField = (name, value) => setSections((prev) => ({ ...prev, [current.key]: { ...prev[current.key], [name]: value } }));

  const saveSection = async (completed) => {
    setSaving(true);
    try {
      await api.patch("/trust-portal/intake/section", { section: current.key, data: sections[current.key] || {}, completed });
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const ok = await saveSection(true);
    if (!ok) return;
    if (step < STEPS.length - 1) setStep(step + 1);
    else {
      try {
        await api.post("/trust-portal/intake/submit");
        toast.success("Intake submitted — thank you!");
        navigate("/trust-portal/dashboard");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  };

  if (!loaded) return null;

  return (
    <TrustPortalLayout>
      <div data-testid="trust-portal-intake-page">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-6">Trust Intake</h1>
        <div className="flex items-center gap-3 mb-8 flex-wrap">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`flex items-center gap-2 text-sm ${i === step ? "text-igc-goldText" : "text-igc-muted"}`} data-testid={`trust-portal-step-indicator-${s.key}`} title={`Step ${i + 1} of ${STEPS.length}: ${s.label}.`}>
              {i < step ? <CheckCircle size={16} weight="fill" className="text-igc-success" /> : <Circle size={16} />}
              {s.label}
            </div>
          ))}
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-6 space-y-4" data-testid={`trust-portal-section-${current.key}`}>
          <h2 className="font-serif text-xl text-igc-cream tracking-tight">{current.label}</h2>
          {current.fields.map((f) => (
            <div key={f.name}>
              <label className="text-xs uppercase tracking-widest text-igc-muted mb-1.5 block">{f.label}</label>
              {f.textarea ? (
                <Textarea data-testid={`trust-portal-field-${f.name}`} value={sections[current.key]?.[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} title={f.label} className="bg-igc-surfaceLight border-igc-gold/20" />
              ) : (
                <Input data-testid={`trust-portal-field-${f.name}`} type={f.type || "text"} value={sections[current.key]?.[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} title={f.label} className="bg-igc-surfaceLight border-igc-gold/20" />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-6">
          <Button data-testid="trust-portal-intake-back-button" variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} title="Goes back to the previous section without losing your answers."><ArrowLeft size={16} className="mr-1" /> Back</Button>
          <Button data-testid="trust-portal-intake-next-button" disabled={saving} onClick={handleNext} title={step === STEPS.length - 1 ? "Saves this section and submits your full intake to IGC for review." : "Saves this section and moves you to the next one."} className="bg-igc-gold hover:bg-igc-goldHover text-black">
            {step === STEPS.length - 1 ? "Submit Intake" : "Save & Continue"} <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      </div>
    </TrustPortalLayout>
  );
}
